import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Icon,
  Input,
  ScrollArea,
  Text,
  toastManager,
} from "@salestrak/ui";
import { PageHeader, PageHeaderBack, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupRow } from "./settings-group";
import { PermissionBadges, type Permissions } from "./permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import type { Omittable } from "@salestrak/ui";

type ApiKeyViewProps = Omittable<{
  // The nested object relaxes too — `Omittable` is not recursive, and the
  // create screens hand this a freshly-minted key whose `creationDate` is
  // whatever the mutation returned.
  apiKey: Omittable<{
    name: string;
    permissions: Permissions;
    creationDate?: string | null;
  }>;
  secret?: string | null;
  onDone: () => void;
}>;

export function ApiKeyView({ apiKey, secret, onDone }: ApiKeyViewProps) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  async function handleCopy() {
    if (!secret) return;
    const ok = await copyToClipboard(secret);
    if (!ok) {
      toastManager.add({ title: "Failed to copy to clipboard", type: "error" });
      return;
    }
    toastManager.add({ title: "API key copied to clipboard", type: "success" });
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  }

  const dateLabel = apiKey.creationDate ? formatDate(apiKey.creationDate) : null;

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="API" onClick={onDone} />
          <PageHeaderTitle>{apiKey.name}</PageHeaderTitle>
          {dateLabel ? (
            <PageHeaderSubtitle>Created {dateLabel}</PageHeaderSubtitle>
          ) : null}
        </PageHeader>
        <Box align="center" direction="column" paddingX="md" paddingY="md">
          <Box direction="column" fill="width" gap="xl" maxWidth={800}>
            <SettingsGroup label="Details">
              <SettingsGroupRow label="Key name">
                <Text size="sm">{apiKey.name}</Text>
              </SettingsGroupRow>

              {secret ? (
                <SettingsGroupRow label="API Key">
                  <Box align="center" direction="row" fill="width" gap="sm">
                    <Box fill="width" minWidth={0} shrink>
                      {/* Read-only reveal field: focusing selects the whole
                        * secret so a keyboard copy needs no drag. */}
                      <Input
                        onFocus={(e) => e.currentTarget.select()}
                        readOnly
                        value={secret}
                      />
                    </Box>
                    <Button
                      aria-label="Copy API key"
                      onClick={handleCopy}
                      size="icon-sm"
                      variant="outline"
                    >
                      <Icon name={copied ? "check" : "copy"} size="md" />
                    </Button>
                  </Box>
                </SettingsGroupRow>
              ) : null}

              <SettingsGroupRow label="Permissions">
                <PermissionBadges permissions={apiKey.permissions} />
              </SettingsGroupRow>
            </SettingsGroup>

            {secret ? (
              <Box surface="sunken" padding="md" rounded="md">
                <Box align="start" direction="row" gap="sm">
                  <Icon color="muted" name="triangle-alert" size="md" />
                  <Text size="xs" variant="muted">
                    Copy this key now. You won't be able to see it again.
                  </Text>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
